import { type ComponentProps, useState } from 'react';
import { NotificationPanel } from '@opswat/ui';
import { dayjs } from 'services/dayjs';
import { useNotifications } from 'services/api/Notifications';

const NOTIFICATION_KEY = 'notifications';

const getIds = (): Set<string> => {
    const ids = localStorage.getItem(NOTIFICATION_KEY);

    if (ids) {
        return new Set(JSON.parse(ids));
    }

    return new Set();
};

const setIds = (ids: Set<string>) => {
    localStorage.setItem(NOTIFICATION_KEY, JSON.stringify([...ids]));
};

export const NotificationCenter = () => {
    const [notificationIds, setNotificationIds] = useState(() => getIds());
    const { data } = useNotifications();

    const notificationData = data?.notifications ? [...data.notifications] : [];
    const notifications = notificationData
        .sort((a, b) => b.timestamp - a.timestamp)
        .map((notification) => {
            const createdAt = dayjs(notification.timestamp);
            const timeAgo = createdAt.from(dayjs());
            const isOlder = createdAt.isBefore(dayjs().subtract(24, 'hour'));
            const formattedTime = createdAt.format('MMM D, YYYY [at] h:mm:ss A');
            const timestamp = isOlder ? formattedTime : timeAgo;
            const isRead = notificationIds.has(notification.id);

            return {
                id: notification.id,
                title: notification.title,
                children: <span className='tw:break-all'>{notification.message}</span>,
                timestamp,
                isRead,
            };
        });

    const handleStateChange: ComponentProps<typeof NotificationPanel>['onStateChange'] = (id) => {
        setNotificationIds((prevIds) => {
            const ids = new Set(prevIds);

            if (id) {
                ids.add(id);
            } else {
                notificationData.forEach(({ id }) => ids.add(id));
            }

            setIds(ids);
            return ids;
        });
    };

    return <NotificationPanel data={notifications} onStateChange={handleStateChange} />;
};
